import { Boxes, Search } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import type { ReleaseArtifactRow } from '../api';
import { StatusChip } from '../components/Avatar';
import { CopyButton } from '../components/CopyButton';
import { EmptyState } from '../components/EmptyState';
import { SectionCard } from '../components/SectionCard';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../hooks/useActionDialog';
import {
  formatArtifactSummary,
  getArtifactTechnicalId,
} from '../utils/formatters';

type ReleasesPageProps = {
  openDialog: UseActionDialogReturn['openDialog'];
};

function ReleasesPage({ openDialog }: ReleasesPageProps) {
  const {
    workspace,
    sortedArtifacts,
    releaseAppVersionById,
    releaseAppById,
    channels,
  } = useWorkspace();

  const { artifacts } = workspace;
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'live' | 'ready'>('all');

  const liveTargetCountByArtifact = useMemo(() => {
    const counts = new Map<string, number>();
    for (const channel of channels) {
      if (!channel.isActive || !channel.releaseArtifactId) continue;
      counts.set(
        channel.releaseArtifactId,
        (counts.get(channel.releaseArtifactId) ?? 0) + 1
      );
    }
    return counts;
  }, [channels]);

  const visibleArtifacts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return sortedArtifacts.filter((artifact) => {
      const isLive = (liveTargetCountByArtifact.get(artifact.releaseArtifactId) ?? 0) > 0;
      if (statusFilter === 'live' && !isLive) return false;
      if (statusFilter === 'ready' && isLive) return false;
      if (!normalizedQuery) return true;
      const version = artifact.releaseAppVersionId
        ? releaseAppVersionById.get(artifact.releaseAppVersionId)
        : undefined;
      const app = version ? releaseAppById.get(version.releaseAppId) : undefined;
      return [
        artifact.version,
        artifact.buildId,
        artifact.gitBranch,
        version?.version,
        app?.name,
        app?.platform,
      ].some((value) => value?.toLowerCase().includes(normalizedQuery));
    });
  }, [
    liveTargetCountByArtifact,
    query,
    releaseAppById,
    releaseAppVersionById,
    sortedArtifacts,
    statusFilter,
  ]);

  function handleDeploy(artifact?: ReleaseArtifactRow) {
    openDialog({
      kind: 'promotion',
      releaseArtifactId:
        artifact?.releaseArtifactId || sortedArtifacts[0]?.releaseArtifactId || '',
      releaseAppVersionId: artifact?.releaseAppVersionId || '',
      environment: '',
    });
  }

  return (
    <SectionCard
      label="Builds from CI"
      title="Bundles"
      description={
        artifacts.length > 0
          ? `${artifacts.length} available · ${liveTargetCountByArtifact.size} currently in use`
          : 'Bundles uploaded by your build pipeline will appear here.'
      }
      action={
        artifacts.length > 0 ? (
          <div className="bundleToolbar">
            <label className="bundleSearch">
              <Search size={14} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search bundle, app, version, or branch"
                aria-label="Search bundles"
              />
            </label>
            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(event.target.value as 'all' | 'live' | 'ready')
              }
              aria-label="Filter bundles by status"
            >
              <option value="all">All statuses</option>
              <option value="live">In use</option>
              <option value="ready">Not deployed</option>
            </select>
          </div>
        ) : undefined
      }
    >
      {artifacts.length === 0 ? (
        <EmptyState
          icon={<Boxes size={20} />}
          title="No bundles uploaded yet"
          description="Upload a compiled JavaScript bundle from CI to make it available for deployment."
        />
      ) : visibleArtifacts.length === 0 ? (
        <EmptyState
          compact
          icon={<Search size={18} />}
          title="No matching bundles"
          description="Try another bundle, app, version, branch, or status."
          action={
            <button
              type="button"
              className="secondaryButton"
              onClick={() => {
                setQuery('');
                setStatusFilter('all');
              }}
            >
              Clear filters
            </button>
          }
        />
      ) : (
        <div className="releaseTableWrap" role="region" aria-label="Release bundles table" tabIndex={0}>
          <table className="releaseTable releaseTableBundles">
            <thead>
              <tr>
                <th>Bundle</th>
                <th>Version / commit</th>
                <th>Current use</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {visibleArtifacts.map((artifact) => {
                const liveTargets =
                  liveTargetCountByArtifact.get(artifact.releaseArtifactId) ?? 0;

                return (
                  <tr key={artifact.releaseArtifactId}>
                    <td>
                      <div className="releaseIdentity">
                        <strong>{formatArtifactSummary(artifact)}</strong>
                        <span className="releaseCodeLine">
                          Exact build ID
                          <CopyButton
                            value={getArtifactTechnicalId(artifact)}
                            label="Copy exact build ID"
                          />
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="releaseIdentity">
                        <strong>{artifact.version || 'Version not recorded'}</strong>
                        <span className="releaseCodeLine">
                          {artifact.gitSha
                            ? `Commit ${artifact.gitSha.slice(0, 10)}`
                            : 'Commit not recorded'}
                          {artifact.gitSha ? (
                            <CopyButton
                              value={artifact.gitSha}
                              label="Copy commit SHA"
                            />
                          ) : null}
                        </span>
                      </div>
                    </td>
                    <td>
                      {liveTargets > 0 ? (
                        <StatusChip tone="success">
                          {liveTargets} live target{liveTargets === 1 ? '' : 's'}
                        </StatusChip>
                      ) : (
                        <StatusChip tone="neutral">Not deployed</StatusChip>
                      )}
                    </td>
                    <td className="releaseTableAction">
                      <button
                        type="button"
                        className="secondaryButton"
                        onClick={() => handleDeploy(artifact)}
                      >
                        Deploy
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

export { ReleasesPage };
